/* BeeMax Wafer theme - small dependency-free helpers: hero slider, flavor carousel, video, reveal on scroll, form prefill. */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var instances = new WeakMap();

  /* ------------------------------------------------------------------ hero slider */
  function Slider(el) {
    var track = el.querySelector('[data-bw-track]');
    if (!track) return;
    var slides = Array.prototype.slice.call(track.querySelectorAll('[data-bw-slide]'));
    if (!slides.length) return;
    var dots = Array.prototype.slice.call(el.querySelectorAll('[data-bw-dot]'));
    var prev = el.querySelector('[data-bw-prev]');
    var next = el.querySelector('[data-bw-next]');
    var pauseBtn = el.querySelector('[data-bw-pause]');
    var countEl = el.querySelector('[data-bw-count]');
    var delay = Math.max(3, parseInt(el.getAttribute('data-delay'), 10) || 7) * 1000;
    var wantAuto = el.getAttribute('data-autoplay') === 'true' && !reduceMotion && slides.length > 1;
    var index = 0, timer = null, userPaused = false, hovering = false, visible = true, raf = 0;

    function width() { return track.clientWidth || 1; }
    function current() { return Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / width()))); }

    function paint(i) {
      index = i;
      slides.forEach(function (s, k) {
        var on = k === i;
        s.classList.toggle('is-active', on);
        if (on) { s.removeAttribute('inert'); s.removeAttribute('aria-hidden'); } else { s.setAttribute('inert', ''); s.setAttribute('aria-hidden', 'true'); }
      });
      if (countEl) countEl.textContent = (i + 1) + ' / ' + slides.length;
      dots.forEach(function (d, k) {
        d.classList.toggle('is-active', k === i);
        if (k === i) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
      });
      // the hero edge (drip, wave ...) takes the colour of the visible slide
      var bg = getComputedStyle(slides[i]).getPropertyValue('--bg');
      if (bg) el.style.setProperty('--bg', bg.trim());
    }

    function go(i, smooth) {
      i = (i + slides.length) % slides.length;
      track.scrollTo({ left: i * width(), behavior: smooth === false || reduceMotion ? 'auto' : 'smooth' });
      paint(i);
    }

    function onScroll() {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () { var i = current(); if (i !== index) paint(i); });
    }

    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function start() {
      stop();
      if (!wantAuto || userPaused || hovering || !visible || document.hidden) return;
      timer = setInterval(function () { go(index + 1); }, delay);
    }

    track.addEventListener('scroll', onScroll, { passive: true });
    track.addEventListener('pointerdown', function () { stop(); }, { passive: true });
    track.addEventListener('pointerup', function () { start(); }, { passive: true });
    el.addEventListener('mouseenter', function () { hovering = true; stop(); });
    el.addEventListener('mouseleave', function () { hovering = false; start(); });
    el.addEventListener('focusin', function () { hovering = true; stop(); });
    el.addEventListener('focusout', function () { hovering = false; start(); });
    document.addEventListener('visibilitychange', start);
    if (prev) prev.addEventListener('click', function () { go(index - 1); });
    if (next) next.addEventListener('click', function () { go(index + 1); });
    dots.forEach(function (d, k) { d.addEventListener('click', function () { go(k); }); });
    if (pauseBtn) pauseBtn.addEventListener('click', function () {
      userPaused = !userPaused;
      pauseBtn.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
      pauseBtn.setAttribute('aria-label', userPaused ? 'Play automatic slides' : 'Pause automatic slides');
      if (userPaused) stop(); else start();
    });
    window.addEventListener('resize', function () { track.scrollTo({ left: index * width(), behavior: 'auto' }); });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) { visible = entries[0].isIntersecting; if (visible) start(); else stop(); }, { threshold: 0.2 }).observe(el);
    }
    paint(0);
    start();
    instances.set(el, { go: go });
  }

  /* ------------------------------------------------------------------ flavor carousel */
  function Carousel(el) {
    var track = el.querySelector('[data-bw-track]');
    if (!track) return;
    var prev = el.querySelector('[data-bw-prev]');
    var next = el.querySelector('[data-bw-next]');
    var thumb = el.querySelector('[data-bw-thumb]');
    var raf = 0;

    function step() {
      var card = track.querySelector('li');
      var gap = parseFloat(getComputedStyle(track).columnGap) || 16;
      return card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
    }
    function update() {
      var max = track.scrollWidth - track.clientWidth;
      var pos = track.scrollLeft;
      if (prev) prev.disabled = pos <= 2;
      if (next) next.disabled = pos >= max - 2;
      if (thumb) {
        var w = Math.max(14, Math.min(100, (track.clientWidth / Math.max(track.scrollWidth, 1)) * 100));
        thumb.style.width = w + '%';
        thumb.style.marginLeft = (max > 0 ? (pos / max) * (100 - w) : 0) + '%';
      }
    }
    function onScroll() { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); }
    track.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: reduceMotion ? 'auto' : 'smooth' }); });
    if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: reduceMotion ? 'auto' : 'smooth' }); });
    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { track.scrollBy({ left: step(), behavior: 'smooth' }); e.preventDefault(); }
      if (e.key === 'ArrowLeft') { track.scrollBy({ left: -step(), behavior: 'smooth' }); e.preventDefault(); }
    });
    update();
  }

  /* ------------------------------------------------------------------ video: muted autoplay while on screen, own play and sound buttons */
  function Video(frame) {
    var v = frame.querySelector('[data-bw-video-el]');
    var playBtn = frame.querySelector('[data-bw-video-play]');
    var soundBtn = frame.querySelector('[data-bw-video-sound]');
    var status = frame.querySelector('[data-bw-video-status]');
    if (!v || !playBtn || !soundBtn) return;
    v.removeAttribute('controls');
    v.controls = false;
    frame.classList.add('has-js');
    v.muted = true;
    v.defaultMuted = true;
    if (reduceMotion) v.loop = false;
    var wantAuto = frame.getAttribute('data-autoplay') === 'true' && !reduceMotion;
    var userPaused = false, visible = false, failed = 0;

    function say(t) { if (status) status.textContent = t; }
    function paint() {
      var playing = !v.paused && !v.ended;
      var sound = !v.muted && v.volume > 0;
      frame.classList.toggle('is-playing', playing);
      frame.classList.toggle('is-sound', sound);
      playBtn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
      soundBtn.setAttribute('aria-pressed', sound ? 'true' : 'false');
    }
    function tryPlay() { var p = v.play(); if (p && p.catch) p.catch(function () { paint(); }); }
    function hide() { var sec = frame.closest('[data-bw-video-sec]') || frame; sec.setAttribute('hidden', ''); sec.style.display = 'none'; }

    playBtn.addEventListener('click', function () {
      if (v.paused || v.ended) { userPaused = false; tryPlay(); say('Video playing'); }
      else { userPaused = true; v.pause(); say('Video paused'); }
    });
    v.addEventListener('click', function () { playBtn.click(); });
    soundBtn.addEventListener('click', function () {
      v.muted = !v.muted;
      if (!v.muted && v.volume === 0) v.volume = 1;
      say(v.muted ? 'Sound off' : 'Sound on');
      paint();
    });
    ['play', 'playing', 'pause', 'ended', 'volumechange'].forEach(function (n) { v.addEventListener(n, paint); });
    /* a file that cannot be loaded: hide the whole section, never leave a broken player */
    var sources = v.querySelectorAll('source');
    Array.prototype.forEach.call(sources, function (s) { s.addEventListener('error', function () { failed++; if (failed >= sources.length) hide(); }); });
    v.addEventListener('error', hide);

    if (wantAuto && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        if (visible && !userPaused && v.paused && v.muted) tryPlay();
        else if (!visible && !v.paused) v.pause();
      }, { threshold: 0.35 }).observe(frame);
    }
    document.addEventListener('visibilitychange', function () {
      if (document.hidden && !v.paused) v.pause();
      else if (!document.hidden && wantAuto && visible && !userPaused && v.paused && v.muted) tryPlay();
    });
    paint();
  }

  /* ------------------------------------------------------------------ reveal on scroll */
  function initReveal() {
    if (reduceMotion || !('IntersectionObserver' in window) || !root.classList.contains('bw-motion')) return;
    var vh = window.innerHeight || 800;
    var targets = document.querySelectorAll('.bw-sec .bw-head, .bw-card, .bw-benefit, .bw-family, .bw-step, .bw-show, .bw-tile, .bw-ticket, .bw-glance__item, .bw-disc, .bw-stat, .bw-trust__item, .bw-panel, .bw-qa');
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    root.classList.add('bw-js');
    Array.prototype.forEach.call(targets, function (t) {
      if (t.getBoundingClientRect().top < vh * 0.92) return;          // already on screen: never hide it
      t.setAttribute('data-bw-reveal', '');
      io.observe(t);
    });
  }

  /* ------------------------------------------------------------------ form prefill from the link (?product=..&sku=..&show=..) */
  function initPrefill() {
    var q;
    try { q = new URLSearchParams(window.location.search); } catch (e) { return; }
    var product = q.get('product'), sku = q.get('sku'), show = q.get('show');
    if (!product && !show) return;
    var text = show ? 'I would like to meet you at ' + show + '. ' : 'I am interested in ' + product + (sku ? ' (SKU ' + sku + ')' : '') + '. ';
    Array.prototype.forEach.call(document.querySelectorAll('[data-bw-prefill="message"]'), function (ta) { if (!ta.value) ta.value = text; });
  }


  /* ------------------------------------------------------------------ engine carousels: unique accessible names */
  function labelCarousels() {
    var seen = {};
    Array.prototype.forEach.call(document.querySelectorAll('.splide[role="region"]'), function (el) {
      var name = el.getAttribute('aria-label');
      if (!name && !el.getAttribute('aria-labelledby')) {
        var id = el.id || '';
        name = /__main$/.test(id) ? 'Product images' : /recs|recommend/.test(id) ? 'More to explore' : /quick-view/.test(id) ? 'Quick view images' : /slideshow-card/.test(id) ? 'Product photos' : 'Image slider';
        el.setAttribute('aria-label', name);
      }
      if (name) { seen[name] = (seen[name] || 0) + 1; if (seen[name] > 1 && !/ \d+$/.test(name)) el.setAttribute('aria-label', name + ' ' + seen[name]); }
    });
  }
  function watchCarousels() {
    labelCarousels();
    if (!window.MutationObserver) return;
    var queued = false;
    new MutationObserver(function () { if (queued) return; queued = true; setTimeout(function () { queued = false; labelCarousels(); }, 150); })
      .observe(document.body, { subtree: true, attributes: true, attributeFilter: ['role'] });
  }


  /* ------------------------------------------------------------------ mobile menu: keyboard (Escape closes, focus moves in and back) */
  function initMenuKeys() {
    document.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest('#mobile-navigation-toggle');
      if (!t) return;
      setTimeout(function () { var a = document.querySelector('#mobile-navigation a, #mobile-navigation button'); if (a) a.focus(); }, 450);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !window.Alpine) return;
      try {
        var nav = window.Alpine.store('xMobileNav');
        if (nav && nav.show) { nav.close(); var tg = document.getElementById('mobile-navigation-toggle'); if (tg) tg.focus(); }
      } catch (err) { /* menu not ready */ }
    });
  }

  /* ------------------------------------------------------------------ boot + theme editor */
  function initAll(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-bw-slider]'), function (el) { if (!instances.has(el)) Slider(el); });
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-bw-carousel]'), function (el) { if (!instances.has(el)) { instances.set(el, true); Carousel(el); } });
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-bw-video]'), function (el) { if (!instances.has(el)) { instances.set(el, true); Video(el); } });
  }
  function boot() { initAll(); initReveal(); initPrefill(); watchCarousels(); initMenuKeys(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); initPrefill(); });
  document.addEventListener('shopify:block:select', function (e) {
    var slide = e.target.closest && e.target.closest('[data-bw-slide]');
    var slider = slide && slide.closest('[data-bw-slider]');
    var inst = slider && instances.get(slider);
    if (inst) { var list = slider.querySelectorAll('[data-bw-slide]'); inst.go(Array.prototype.indexOf.call(list, slide), false); }
  });
})();
